import { useCallback, useEffect, useMemo, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useActiveTabVisibility } from './useActiveTabVisibility';
import { ApiError, api } from '../api';
import type {
  ProcessingAvailability,
  ProcessingAvailabilityInput,
  ProcessingAvailabilityMode,
  HostedProcessingOverview,
  HostedProcessingQueueJob,
  ProcessingMode,
  ProcessingWorker,
  ProcessingWorkersOverview,
  WorkerEnrollment,
} from '../types';
import { Icon, type IconName } from './Icon';
import { Modal } from './Modal';

interface ProcessingWorkersPanelProps {
  notify: (message: string, tone?: 'success' | 'error') => void;
  hostedSchedulingEnabled?: boolean;
  view?: ProcessingDashboardView;
  onViewChange?: (view: ProcessingDashboardView) => void;
}

type RefreshKind = 'initial' | 'manual' | 'poll';
type WorkerState = 'online' | 'offline' | 'revoked';
type QueueAction =
  | { type: 'convertNow'; job: HostedProcessingQueueJob }
  | { type: 'pause'; pause: boolean };
export type AgentPairingPlatform = 'macos' | 'linux' | 'windows';
export type ProcessingDashboardView = 'queue' | 'availability' | 'workers' | 'setup';

const PROCESSING_DASHBOARD_VIEWS: Array<{
  id: ProcessingDashboardView;
  label: string;
  description: string;
  icon: IconName;
  hostedOnly?: boolean;
}> = [
  { id: 'queue', label: 'Queue', description: 'Queued work and audit', icon: 'list', hostedOnly: true },
  { id: 'availability', label: 'Availability', description: 'Schedule and claim rules', icon: 'play' },
  { id: 'workers', label: 'Workers', description: 'Enrollment and inventory', icon: 'server' },
  { id: 'setup', label: 'Install Agent', description: 'Computer setup guide', icon: 'hard-drive' },
];

const AGENT_INSTALLER_URL = '/api/processing-workers/installer';
const DOCKER_INSTALL_URLS: Record<AgentPairingPlatform, string> = {
  macos: 'https://docs.docker.com/desktop/setup/install/mac-install/',
  windows: 'https://docs.docker.com/desktop/setup/install/windows-install/',
  linux: 'https://docs.docker.com/engine/install/',
};

const WORKER_NAME_PATTERN = /^[\p{L}\p{N}][\p{L}\p{N} ._:-]*$/u;

function errorMessage(reason: unknown, fallback: string): string {
  return reason instanceof ApiError ? reason.message : fallback;
}

function formatTimestamp(value?: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return 'Unknown';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function formatScheduleTimestamp(value: string | null, timezone: string): string {
  if (!value) return 'an unknown time';
  const date = new Date(value);
  if (Number.isNaN(date.valueOf())) return 'an unknown time';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: timezone,
  }).format(date);
}

function availabilityTitle(availability: ProcessingAvailability, videoOnly = false): string {
  if (availability.reason === 'manual_override') return videoOnly ? 'Video override active' : 'All-queue override active';
  if (availability.reason === 'always_available') return videoOnly ? 'Video conversion is always available' : 'Queue is always open';
  if (availability.reason === 'inside_schedule') return videoOnly ? 'Video conversion window is open' : 'Scheduled window is open';
  if (availability.reason === 'paused') return videoOnly ? 'Video conversion claims are paused' : 'New job claims are paused';
  return videoOnly ? 'Waiting for the next video window' : 'Waiting for the next window';
}

function availabilityExplanation(availability: ProcessingAvailability, videoOnly = false): string {
  if (availability.reason === 'manual_override') return videoOnly
    ? `A legacy override is opening regular video conversion until ${formatScheduleTimestamp(availability.windowEndsAt, availability.timezone)} (${availability.timezone}). End it to return to the saved schedule; targeted Convert now and subtitle jobs do not need this override.`
    : `A legacy override is opening the entire regular queue until ${formatScheduleTimestamp(availability.windowEndsAt, availability.timezone)} (${availability.timezone}). End it to return to the saved schedule; targeted Convert now jobs do not need this override.`;
  if (availability.reason === 'always_available') return videoOnly ? 'A worker can claim a queued video conversion whenever the shared processing slot is idle.' : 'A processing worker can claim a queued job whenever one is available.';
  if (availability.reason === 'inside_schedule') return videoOnly
    ? `New video conversions can be claimed until ${formatScheduleTimestamp(availability.windowEndsAt, availability.timezone)} (${availability.timezone}). Active work continues after the window closes.`
    : `New jobs can be claimed until ${formatScheduleTimestamp(availability.windowEndsAt, availability.timezone)} (${availability.timezone}). Active jobs continue after the window closes.`;
  if (availability.reason === 'paused') return videoOnly ? 'No regular video conversion will start. Subtitle jobs may still run when the shared processing slot is idle.' : 'No new jobs will start. Any job already processing can finish normally.';
  return videoOnly
    ? `The next video conversion window starts ${formatScheduleTimestamp(availability.nextClaimAt, availability.timezone)} (${availability.timezone}). Subtitle jobs remain eligible while Agents are online and the shared slot is idle.`
    : `The next claim window starts ${formatScheduleTimestamp(availability.nextClaimAt, availability.timezone)} (${availability.timezone}). Agents remain online while they wait.`;
}

function ownerActionLabel(eventType: string): string {
  if (eventType === 'convert_now_requested') return 'Convert now requested';
  if (eventType === 'owner_retry_approved') return 'Owner retry approved';
  if (eventType === 'pause_after_current_enabled') return 'Pause after current enabled';
  if (eventType === 'pause_after_current_cleared') return 'Fair queue resumed';
  return eventType.replaceAll('_', ' ');
}

function timezoneOptions(current?: string): string[] {
  const common = ['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura', 'Asia/Singapore', 'Etc/UTC'];
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] };
  const supported = intl.supportedValuesOf?.('timeZone') ?? [];
  return [...new Set([current, browserTimezone, ...common, ...supported].filter((value): value is string => Boolean(value)))];
}

function workerState(worker: ProcessingWorker): WorkerState {
  if (worker.revokedAt || worker.status === 'revoked') return 'revoked';
  return worker.online ? 'online' : 'offline';
}

function stateTone(state: WorkerState): 'ready' | 'failed' | 'neutral' {
  if (state === 'online') return 'ready';
  if (state === 'revoked') return 'failed';
  return 'neutral';
}

function modeTitle(mode: ProcessingMode): string {
  if (mode === 'external') return 'External processing';
  if (mode === 'vps') return 'VPS processing';
  if (mode === 'disabled') return 'Processing disabled';
  return 'Processing mode unavailable';
}

function modeExplanation(mode: ProcessingMode): string {
  if (mode === 'external') return 'VPS FFmpeg is disabled. Online external agents claim queued videos and perform conversion outside the production server.';
  if (mode === 'vps') return 'The VPS FFmpeg worker claims queued videos. External agents remain connected but cannot claim while this mode is active.';
  if (mode === 'disabled') return 'Video processing is disabled. Queued videos will not be claimed by the VPS or a local agent.';
  return 'The server did not report a recognized processing mode.';
}

function agentServerUrl(): string {
  const url = new URL(window.location.origin);
  if (['localhost', '127.0.0.1'].includes(url.hostname)) url.hostname = 'host.docker.internal';
  return url.origin;
}

function defaultPairingPlatform(): AgentPairingPlatform {
  if (/Windows/i.test(navigator.userAgent)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(navigator.userAgent)) return 'macos';
  return 'linux';
}

function posixQuote(value: string): string {
  return `'${value.replaceAll("'", `'"'"'`)}'`;
}

function powershellQuote(value: string): string {
  return `'${value.replaceAll("'", "''")}'`;
}

export function agentInstallerCommands(platform: AgentPairingPlatform, serverUrl: string): string {
  if (platform === 'windows') {
    return `powershell -NoProfile -ExecutionPolicy Bypass -File .\\install.ps1 -ServerUrl ${powershellQuote(serverUrl)}`;
  }
  return `./install.sh --server-url ${posixQuote(serverUrl)}`;
}

export function agentDockerInstallUrl(platform: AgentPairingPlatform): string {
  return DOCKER_INSTALL_URLS[platform];
}

export function agentPairingCommands(platform: AgentPairingPlatform, workerName = 'Local worker'): string {
  if (platform === 'windows') {
    return `powershell -NoProfile -ExecutionPolicy Bypass -File "$env:LOCALAPPDATA\\DeweStream\\Agent\\dewe-agent.ps1" pair --worker-name ${powershellQuote(workerName)}`;
  }
  return `"$HOME/.local/bin/dewe-agent" pair --worker-name ${posixQuote(workerName)}`;
}

export function agentOpenAICommands(platform: AgentPairingPlatform): string {
  if (platform === 'windows') {
    return 'powershell -NoProfile -ExecutionPolicy Bypass -File "$env:LOCALAPPDATA\\DeweStream\\Agent\\dewe-agent.ps1" configure-openai';
  }
  return '"$HOME/.local/bin/dewe-agent" configure-openai';
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const field = document.createElement('textarea');
  field.value = value;
  field.style.position = 'fixed';
  field.style.opacity = '0';
  document.body.appendChild(field);
  field.select();
  const copied = document.execCommand('copy');
  field.remove();
  if (!copied) throw new Error('Copy failed');
}

export function ProcessingWorkersPanel({ notify, hostedSchedulingEnabled = false, view = 'queue', onViewChange }: ProcessingWorkersPanelProps) {
  const [internalView, setInternalView] = useState<ProcessingDashboardView>(view);
  const [overview, setOverview] = useState<ProcessingWorkersOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [workerName, setWorkerName] = useState('');
  const [creating, setCreating] = useState(false);
  const [enrollment, setEnrollment] = useState<(WorkerEnrollment & { workerName: string }) | null>(null);
  const [revokingId, setRevokingId] = useState('');
  const [showRevoked, setShowRevoked] = useState(false);
  const [pairingPlatform, setPairingPlatform] = useState<AgentPairingPlatform>(defaultPairingPlatform);
  const [availabilityDraft, setAvailabilityDraft] = useState<ProcessingAvailabilityInput>({
    mode: 'always',
    timezone: 'Asia/Jakarta',
    windowStart: '00:00',
    windowEnd: '06:00',
  });
  const [availabilityDirty, setAvailabilityDirty] = useState(false);
  const [savingAvailability, setSavingAvailability] = useState(false);
  const [changingOverride, setChangingOverride] = useState(false);
  const [hostedScheduling, setHostedScheduling] = useState<HostedProcessingOverview | null>(null);
  const [pendingQueueAction, setPendingQueueAction] = useState<QueueAction | null>(null);
  const [queueActionReason, setQueueActionReason] = useState('');
  const [queueActionPassword, setQueueActionPassword] = useState('');
  const [queueActionError, setQueueActionError] = useState('');
  const [schedulingBusy, setSchedulingBusy] = useState(false);

  useEffect(() => { setInternalView(view); }, [view]);

  const pairingCommands = useMemo(() => {
    if (!enrollment) return '';
    return agentPairingCommands(pairingPlatform, enrollment.workerName);
  }, [enrollment, pairingPlatform]);
  const openAICommands = useMemo(() => agentOpenAICommands(pairingPlatform), [pairingPlatform]);
  const installerCommands = useMemo(() => agentInstallerCommands(pairingPlatform, agentServerUrl()), [pairingPlatform]);

  const loadWorkers = useCallback(async (kind: RefreshKind = 'manual') => {
    if (kind === 'initial') setLoading(true);
    if (kind === 'manual') setRefreshing(true);
    if (kind !== 'poll') setLoadError('');
    try {
      const [workers, scheduling] = await Promise.all([
        api.processingWorkers(),
        hostedSchedulingEnabled ? api.hostedProcessingOverview() : Promise.resolve(null),
      ]);
      setOverview(workers);
      setHostedScheduling(scheduling);
      setLoadError('');
    } catch (reason) {
      setLoadError(errorMessage(reason, 'Unable to load processing workers.'));
    } finally {
      if (kind === 'initial') setLoading(false);
      if (kind === 'manual') setRefreshing(false);
    }
  }, [hostedSchedulingEnabled]);

  useEffect(() => {
    void loadWorkers('initial');
    const timer = window.setInterval(() => void loadWorkers('poll'), 30_000);
    return () => window.clearInterval(timer);
  }, [loadWorkers]);

  useEffect(() => {
    if (!overview || availabilityDirty) return;
    setAvailabilityDraft({
      mode: overview.availability.mode,
      timezone: overview.availability.timezone,
      windowStart: overview.availability.windowStart,
      windowEnd: overview.availability.windowEnd,
    });
  }, [overview, availabilityDirty]);

  function changeAvailability<K extends keyof ProcessingAvailabilityInput>(key: K, value: ProcessingAvailabilityInput[K]) {
    setAvailabilityDraft((current) => ({ ...current, [key]: value }));
    setAvailabilityDirty(true);
  }

  function applyAvailability(availability: ProcessingAvailability): void {
    setOverview((current) => current ? {
      ...current,
      availability,
      jobClaimEnabled: current.processingMode === 'external' && availability.claimEligible,
      serverTime: new Date().toISOString(),
    } : current);
  }

  async function saveAvailability(event: FormEvent) {
    event.preventDefault();
    setSavingAvailability(true);
    setActionError('');
    try {
      const availability = await api.updateProcessingAvailability(availabilityDraft);
      applyAvailability(availability);
      setAvailabilityDirty(false);
      notify('Processing availability saved.');
    } catch (reason) {
      setActionError(errorMessage(reason, 'Unable to save processing availability.'));
    } finally {
      setSavingAvailability(false);
    }
  }

  async function endLegacyOverride() {
    if (!overview) return;
    setChangingOverride(true);
    setActionError('');
    try {
      const availability = await api.stopProcessingOverride();
      applyAvailability(availability);
      notify('All-queue override ended. The regular queue now follows the saved availability.');
    } catch (reason) {
      setActionError(errorMessage(reason, 'Unable to end the all-queue override.'));
    } finally {
      setChangingOverride(false);
    }
  }

  async function submitQueueAction(event: FormEvent) {
    event.preventDefault();
    if (!pendingQueueAction) return;
    const reason = queueActionReason.trim();
    if (reason.length < 8) {
      setQueueActionError('Give an 8–500 character reason for this Owner action.');
      return;
    }
    if (!queueActionPassword) {
      setQueueActionError('Enter your current Owner password to continue.');
      return;
    }
    setSchedulingBusy(true);
    setActionError('');
    setQueueActionError('');
    try {
      await api.reauthenticate(queueActionPassword);
      setQueueActionPassword('');
      if (pendingQueueAction.type === 'convertNow') {
        await api.requestHostedConvertNow(pendingQueueAction.job.kind, pendingQueueAction.job.jobId, reason);
      } else {
        await api.setHostedPauseAfterCurrent(pendingQueueAction.pause, reason);
      }
      const completedAction = pendingQueueAction;
      setPendingQueueAction(null);
      setQueueActionReason('');
      setQueueActionError('');
      await loadWorkers('poll');
      if (completedAction.type === 'convertNow') notify('Video added to the manual conversion queue. It can start outside the regular schedule.');
      else notify(completedAction.pause ? 'Queue will pause after the active job.' : 'Queue may claim work again when the window is open.');
    } catch (reason) {
      setQueueActionPassword('');
      setQueueActionError(errorMessage(reason, pendingQueueAction.type === 'convertNow' ? 'Unable to add this video to the manual conversion queue.' : 'Unable to update the Hosted queue.'));
    } finally {
      setSchedulingBusy(false);
    }
  }

  async function cancelActive(kind: 'transcode' | 'transcription', jobId: string) {
    if (!window.confirm('Cancel this active job? It stops at the next safe checkpoint and must be restarted from the queue.')) return;
    setSchedulingBusy(true);
    setActionError('');
    try {
      await api.cancelProcessingJob(kind, jobId);
      await loadWorkers('poll');
      notify('Cancellation was requested. The Agent will stop at a safe checkpoint.');
    } catch (reason) {
      setActionError(errorMessage(reason, 'Unable to cancel the active job.'));
    } finally {
      setSchedulingBusy(false);
    }
  }

  async function createEnrollment(event: FormEvent) {
    event.preventDefault();
    const name = workerName.trim().replace(/\s+/g, ' ');
    if (!name) return;
    if (name.length > 100 || !WORKER_NAME_PATTERN.test(name)) {
      setActionError('Worker name must be 1–100 letters, numbers, spaces, or ._:- characters.');
      return;
    }
    setCreating(true);
    setActionError('');
    setEnrollment(null);
    try {
      const result = await api.createProcessingWorkerEnrollment(name);
      setEnrollment({ ...result, workerName: name });
      setWorkerName('');
      notify('One-time enrollment code created.');
    } catch (reason) {
      setActionError(errorMessage(reason, 'Unable to create an enrollment code.'));
    } finally {
      setCreating(false);
    }
  }

  async function copy(value: string, label: string) {
    setActionError('');
    try {
      await copyText(value);
      notify(`${label} copied.`);
    } catch {
      setActionError(`Unable to copy the ${label.toLowerCase()}. Select it and copy it manually.`);
    }
  }

  async function revoke(worker: ProcessingWorker) {
    if (!window.confirm(`Revoke “${worker.name}”? It will no longer be able to send heartbeats and must be enrolled again.`)) return;
    setRevokingId(worker.id);
    setActionError('');
    try {
      await api.revokeProcessingWorker(worker.id);
      notify(`${worker.name} revoked.`);
      await loadWorkers('poll');
    } catch (reason) {
      setActionError(errorMessage(reason, 'Unable to revoke the processing worker.'));
    } finally {
      setRevokingId('');
    }
  }

  const mode = overview?.processingMode ?? 'unknown';
  const revokedWorkerCount = overview?.workers.filter((worker) => workerState(worker) === 'revoked').length ?? 0;
  const visibleWorkers = overview?.workers.filter((worker) => showRevoked || workerState(worker) !== 'revoked') ?? [];
  const visibleViews = PROCESSING_DASHBOARD_VIEWS.filter((item) => !item.hostedOnly || hostedSchedulingEnabled);
  const requestedView = onViewChange ? view : internalView;
  const selectedView = requestedView === 'queue' && !hostedSchedulingEnabled ? 'availability' : requestedView;
  const tabRail = useActiveTabVisibility(selectedView);
  const manualQueue = hostedScheduling?.queued.filter((job) => job.queueAction !== null) ?? [];
  const manualRunActive = manualQueue.length > 0 || Boolean(hostedScheduling?.heavyWork?.queueAction);

  function selectView(nextView: ProcessingDashboardView) {
    setInternalView(nextView);
    onViewChange?.(nextView);
  }

  function handleTabKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    const tabs = Array.from(event.currentTarget.closest('[role="tablist"]')?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? []);
    const index = tabs.indexOf(event.currentTarget);
    const nextIndex = event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
    tabs[nextIndex]?.focus();
    tabs[nextIndex]?.click();
  }

  return <section className="processing-panel app-page" aria-labelledby="processing-workers-title">
    <header className="content-header processing-panel__header">
      <div><span className="eyebrow">Owner controls</span><h1 id="processing-workers-title">Processing operations</h1><p>Review queued work, control availability, and manage processing computers.</p></div>
      <div className="processing-panel__header-actions">
        {overview?.serverTime && <small>Server time {formatTimestamp(overview.serverTime)}</small>}
        <button className="button button--secondary" type="button" onClick={() => void loadWorkers('manual')} disabled={loading || refreshing}><Icon name="refresh" size={17} />{refreshing ? 'Refreshing…' : 'Refresh'}</button>
      </div>
    </header>

    {loadError && <p className="form-alert" role="alert">{loadError}</p>}
    {actionError && <p className="form-alert" role="alert">{actionError}</p>}

    <article className="section-workspace section-workspace--horizontal processing-workspace">
      <div ref={tabRail} className="section-workspace__nav" role="tablist" aria-label="Processing sections">
        {visibleViews.map((item) => <button id={`processing-view-tab-${item.id}`} key={item.id} type="button" role="tab" aria-selected={selectedView === item.id} aria-controls="processing-view-content" tabIndex={selectedView === item.id ? 0 : -1} onKeyDown={handleTabKeyDown} onClick={() => selectView(item.id)}><Icon name={item.icon} size={18} /><span><strong>{item.label}</strong><small>{item.description}</small></span><Icon name="chevron" size={14} /></button>)}
      </div>
      <div id="processing-view-content" className="section-workspace__content section-context-panel" role="tabpanel" aria-labelledby={`processing-view-tab-${selectedView}`}>
    {loading && !overview && <div className="panel-loading"><span className="spinner spinner--large" />Loading processing operations…</div>}

    {selectedView === 'availability' && <article className={`processing-mode-card processing-mode-card--${mode}`}>
      <span className="processing-mode-card__icon"><Icon name="server" size={25} /></span>
      <div><span>Current mode</span><h2>{modeTitle(mode)}</h2><p><strong>{modeExplanation(mode)}</strong></p></div>
      <span className={`status-badge status-badge--${mode === 'vps' ? 'ready' : mode === 'external' ? 'processing' : 'neutral'}`}><span className="status-badge__dot" />{mode}</span>
    </article>}

    {selectedView === 'availability' && overview && <article className="worker-card processing-availability-card">
      <div className="worker-card__heading processing-availability-card__heading">
        <div><h2>{hostedSchedulingEnabled ? 'Video conversion availability' : 'Regular queue availability'}</h2><p>{hostedSchedulingEnabled ? 'Schedule regular video transcoding. Subtitle jobs may run whenever an eligible Agent is online and the shared processing slot is idle.' : 'Control when workers may claim regular video and subtitle jobs. Targeted Convert now jobs may run outside this schedule.'}</p></div>
        <span className={`status-badge status-badge--${overview.availability.claimEligible ? 'ready' : 'neutral'}`}><span className="status-badge__dot" />{overview.availability.claimEligible ? (hostedSchedulingEnabled ? 'Video queue open' : 'Regular queue open') : (hostedSchedulingEnabled ? 'Video queue closed' : 'Regular queue closed')}</span>
      </div>
      <div className={`processing-availability-status ${overview.availability.claimEligible ? 'is-open' : ''}`}>
        <span className="processing-availability-status__icon"><Icon name={overview.availability.claimEligible ? 'play' : 'pause'} size={20} /></span>
        <div><strong>{availabilityTitle(overview.availability, hostedSchedulingEnabled)}</strong><span>{availabilityExplanation(overview.availability, hostedSchedulingEnabled)}</span></div>
      </div>
      <form className="processing-availability-form" onSubmit={saveAvailability}>
        <fieldset className="processing-availability-options">
          <legend>{hostedSchedulingEnabled ? 'When can workers start a new video conversion?' : 'When can workers start a new job?'}</legend>
          {([
            ['always', 'Always available', hostedSchedulingEnabled ? 'Claim queued video conversions at any time.' : 'Claim queued jobs at any time.'],
            ['scheduled', 'Daily schedule', hostedSchedulingEnabled ? 'Open video conversion during one daily time window.' : 'Open the queue during one daily time window.'],
            ['paused', 'Paused', hostedSchedulingEnabled ? 'Keep Agents and subtitles online without starting regular video conversions.' : 'Keep workers online without starting new jobs.'],
          ] as Array<[ProcessingAvailabilityMode, string, string]>).map(([value, label, description]) => <label key={value}>
            <input className="control-choice" type="radio" name="availability-mode" value={value} checked={availabilityDraft.mode === value} onChange={() => changeAvailability('mode', value)} />
            <span><strong>{label}</strong><small>{description}</small></span>
          </label>)}
        </fieldset>
        {availabilityDraft.mode === 'scheduled' && <div className="processing-schedule-fields">
          <label>Timezone<select className="control-select" value={availabilityDraft.timezone} onChange={(event) => changeAvailability('timezone', event.target.value)}>{timezoneOptions(availabilityDraft.timezone).map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}</select><small>The App uses this timezone, independent of the Agent computer clock.</small></label>
          <label>Start<input type="time" value={availabilityDraft.windowStart} onChange={(event) => changeAvailability('windowStart', event.target.value)} required /></label>
          <label>End<input type="time" value={availabilityDraft.windowEnd} onChange={(event) => changeAvailability('windowEnd', event.target.value)} required /><small>An end time earlier than the start time continues into the next day.</small></label>
        </div>}
        <div className="processing-availability-actions">
          <button className="button button--primary" type="submit" disabled={!availabilityDirty || savingAvailability}>{savingAvailability ? 'Saving…' : 'Save availability'}</button>
          {overview.availability.reason === 'manual_override' && <button className="button button--secondary" type="button" onClick={() => void endLegacyOverride()} disabled={changingOverride || availabilityDirty}>{changingOverride ? 'Ending…' : 'End all-queue override'}</button>}
        </div>
        <p className="worker-card__hint"><strong>Convert now is targeted.</strong> It adds only the selected job to the manual FIFO queue; it does not open the rest of the regular queue.</p>
        {hostedSchedulingEnabled && <p className="worker-card__hint"><strong>AI subtitles are schedule-independent.</strong> They can start whenever a compatible Agent is online, but still wait while a transcode or another subtitle job owns the shared processing slot.</p>}
      </form>
    </article>}

    {selectedView === 'queue' && hostedScheduling && <article className="worker-card processing-availability-card">
      <div className="worker-card__heading processing-availability-card__heading">
        <div><h2>Hosted shared queue</h2><p>One heavy task runs at a time. Convert now jobs use a separate Owner FIFO and run before the regular scheduled queue.</p></div>
        <span className={`status-badge status-badge--${hostedScheduling.control.pauseAfterCurrent ? 'neutral' : 'ready'}`}><span className="status-badge__dot" />{hostedScheduling.control.pauseAfterCurrent ? 'Pausing after current' : manualRunActive ? 'Manual run active' : 'Fair queue active'}</span>
      </div>
      <div className="processing-availability-actions">
        <button className="button button--secondary" type="button" disabled={schedulingBusy} onClick={() => { setQueueActionReason(''); setPendingQueueAction({ type: 'pause', pause: !hostedScheduling.control.pauseAfterCurrent }); }}>{hostedScheduling.control.pauseAfterCurrent ? 'Resume fair queue' : 'Pause after current job'}</button>
        {hostedScheduling.heavyWork && <button className="button button--danger" type="button" disabled={schedulingBusy} onClick={() => void cancelActive(hostedScheduling.heavyWork!.kind, hostedScheduling.heavyWork!.jobId)}>Cancel active job</button>}
      </div>
      {hostedScheduling.heavyWork && <p className="worker-card__hint">Active {hostedScheduling.heavyWork.kind} job: <code>{hostedScheduling.heavyWork.jobId}</code>. Cancellation preserves the source and returns or cancels work according to its current job state.</p>}
      <div className="workers-table-wrap"><table className="video-table workers-table processing-queue-table"><thead><tr><th>Member</th><th>Video</th><th>Work</th><th>Requested</th><th>Action</th></tr></thead><tbody>{hostedScheduling.queued.slice(0, 12).map((job) => <tr key={`${job.kind}:${job.jobId}`}>
        <td data-label="Member"><div className="worker-identity"><strong>{job.memberName}</strong><span>{job.memberEmail}</span></div></td>
        <td data-label="Video"><div className="worker-identity"><strong>{job.videoTitle}</strong><span>{job.folderPath}</span><code>{job.videoId}</code></div></td>
        <td data-label="Work"><strong>{job.kind}</strong><br /><code>{job.jobId}</code></td>
        <td data-label="Requested">{job.manualRequestedAt ? <div className="worker-identity"><strong>Convert now</strong><time dateTime={job.manualRequestedAt}>{formatTimestamp(job.manualRequestedAt)}</time></div> : <time dateTime={job.createdAt}>{formatTimestamp(job.createdAt)}</time>}</td>
        <td data-label="Action">{job.queueAction ? <span className="status-badge status-badge--processing"><span className="status-badge__dot" />Manual queue #{manualQueue.findIndex((item) => item.jobId === job.jobId) + 1}</span> : <button className="button button--secondary" type="button" disabled={schedulingBusy} onClick={() => { setQueueActionReason(''); setQueueActionPassword(''); setQueueActionError(''); setPendingQueueAction({ type: 'convertNow', job }); }}>Convert now</button>}</td>
      </tr>)}</tbody></table></div>
      {!hostedScheduling.queued.length && <p className="worker-card__hint">No queued work. Convert now remains subject to the Member's processing allowance and storage reservation.</p>}
      <div className="processing-audit">
        <div className="processing-audit__heading"><h3>Recent Owner actions</h3><p>Reasons are retained in the append-only processing audit and remain visible after a job finishes.</p></div>
        {hostedScheduling.recentActions.length ? <div className="workers-table-wrap"><table className="video-table workers-table"><thead><tr><th>Action</th><th>Member / video</th><th>Reason</th><th>When</th></tr></thead><tbody>{hostedScheduling.recentActions.map((action) => <tr key={action.id}>
          <td data-label="Action"><strong>{ownerActionLabel(action.eventType)}</strong><br /><span>{action.actorName || 'Owner'}</span></td>
          <td data-label="Member / video"><div className="worker-identity"><strong>{action.videoTitle || action.memberName || 'Queue control'}</strong>{action.memberEmail && <span>{action.memberEmail}</span>}{action.videoId && <code>{action.videoId}</code>}</div></td>
          <td data-label="Reason"><span className="processing-audit__reason">{action.reason}</span></td>
          <td data-label="When"><time dateTime={action.createdAt}>{formatTimestamp(action.createdAt)}</time></td>
        </tr>)}</tbody></table></div> : <p className="worker-card__hint">No Owner queue actions have been recorded yet.</p>}
      </div>
    </article>}

    <Modal
      open={Boolean(pendingQueueAction)}
      title={pendingQueueAction?.type === 'convertNow' ? 'Convert now' : pendingQueueAction?.pause ? 'Pause after current job' : 'Resume fair queue'}
      description={pendingQueueAction?.type === 'convertNow'
        ? `Add “${pendingQueueAction.job.videoTitle}” to the Owner's manual FIFO. It can start outside the regular schedule as soon as the current heavy job finishes.`
        : pendingQueueAction?.pause
          ? 'Let the active job finish, then prevent the queue from claiming another job.'
          : 'Allow the fair queue to claim work again when processing availability is open.'}
      onClose={() => { if (!schedulingBusy) { setPendingQueueAction(null); setQueueActionReason(''); setQueueActionPassword(''); setQueueActionError(''); } }}
      width="small"
      footer={<><button className="button button--secondary" type="button" disabled={schedulingBusy} onClick={() => { setPendingQueueAction(null); setQueueActionReason(''); setQueueActionPassword(''); setQueueActionError(''); }}>Cancel</button><button className="button button--primary" type="submit" form="hosted-queue-action-form" disabled={schedulingBusy || queueActionReason.trim().length < 8 || !queueActionPassword}>{schedulingBusy ? 'Confirming…' : pendingQueueAction?.type === 'convertNow' ? 'Confirm & convert now' : 'Confirm Owner action'}</button></>}
    >
      <form id="hosted-queue-action-form" className="dialog-form" onSubmit={submitQueueAction}>
        {queueActionError && <p id="hosted-queue-action-error" className="form-alert" role="alert">{queueActionError}</p>}
        <label htmlFor="hosted-queue-action-reason">Reason for this Owner action<textarea id="hosted-queue-action-reason" value={queueActionReason} onChange={(event) => { setQueueActionReason(event.target.value); setQueueActionError(''); }} minLength={8} maxLength={500} rows={4} required autoFocus /><small>Required for accountability. This reason is written to the append-only processing audit and appears in Recent Owner actions.</small></label>
        <label htmlFor="hosted-queue-action-password">Current Owner password<input id="hosted-queue-action-password" type="password" autoComplete="current-password" value={queueActionPassword} onChange={(event) => { setQueueActionPassword(event.target.value); setQueueActionError(''); }} aria-invalid={Boolean(queueActionError)} aria-describedby={`${queueActionError ? 'hosted-queue-action-error ' : ''}hosted-queue-action-password-note`} required /><small id="hosted-queue-action-password-note"><Icon name="shield" size={15} /> Sent only to the recent-authentication endpoint and cleared after this attempt.</small></label>
      </form>
    </Modal>

    {selectedView === 'availability' && overview && <div className="processing-metrics" aria-label="Processing worker summary">
      <article><span>Mode</span><strong>{overview.processingMode}</strong><small>{overview.processingMode === 'external' ? (overview.availability.claimEligible ? 'Regular and targeted claims enabled' : 'Targeted Convert now remains available') : overview.jobClaimEnabled ? 'Job claiming enabled' : 'Job claiming unavailable'}</small></article>
      <article><span>Online agents</span><strong>{overview.counts.onlineWorkers.toLocaleString()}</strong><small>Heartbeat received in the last 90 seconds</small></article>
      <article><span>Active enrollments</span><strong>{overview.counts.totalActiveWorkers.toLocaleString()}</strong><small>Workers that have not been revoked</small></article>
      <article><span>Queued jobs</span><strong>{overview.counts.queuedJobs.toLocaleString()}</strong><small>{overview.processingMode === 'external' ? (overview.counts.onlineWorkers === 0 ? 'Waiting for an online agent' : overview.availability.claimEligible ? 'Regular queue can be claimed' : 'Regular jobs wait; Convert now can run') : 'Waiting to be claimed'}</small></article>
    </div>}

    {selectedView === 'setup' && <article className="worker-card agent-installer-card">
      <div className="worker-card__heading"><div><h2>Set up a processing computer</h2><p>Install the Agent once on the computer that will convert your videos. No source code, IDE, or manual <code>.env</code> editing is required.</p></div><span className="worker-card__heading-icon"><Icon name="hard-drive" size={20} /></span></div>
      <div className="agent-readiness-note"><Icon name="shield" size={17} /><div><strong>Complete this setup before generating an enrollment code</strong><span>The installer creates the private configuration, builds the Agent image, checks the connection to this App, and leaves enrollment for the final step.</span></div></div>
      <div className="pairing-platforms" role="tablist" aria-label="Agent installer operating system">
        {([
          ['macos', 'macOS'],
          ['windows', 'Windows'],
          ['linux', 'Linux'],
        ] as const).map(([value, label]) => <button className={pairingPlatform === value ? 'is-active' : ''} key={value} type="button" role="tab" aria-selected={pairingPlatform === value} onClick={() => setPairingPlatform(value)}>{label}</button>)}
      </div>
      <ol className="agent-installer-steps">
        <li><div className="agent-installer-step"><strong>Install and start Docker</strong><span>{pairingPlatform === 'linux' ? 'Install Docker Engine and the Compose plugin, then make sure your user can run Docker.' : `Install Docker Desktop for ${pairingPlatform === 'macos' ? 'macOS' : 'Windows'}, open it, and wait until the Docker engine is running.`}</span><a className="button button--secondary" href={agentDockerInstallUrl(pairingPlatform)} target="_blank" rel="noreferrer">Open official Docker guide</a></div></li>
        <li><div className="agent-installer-step"><strong>Download the Agent package</strong><span>Save the archive on this processing computer.</span><a className="button button--primary" href={AGENT_INSTALLER_URL} download><Icon name="hard-drive" size={17} />Download Agent package</a></div></li>
        <li><div className="agent-installer-step"><strong>Extract the package</strong><span>Extract <code>dewe-stream-agent-installer.zip</code> with Finder, File Explorer, or your Linux archive manager. Open the resulting <code>dewe-stream-agent-installer</code> folder.</span></div></li>
        <li><div className="agent-installer-step"><strong>Open a terminal in the extracted folder</strong><span>{pairingPlatform === 'windows' ? 'Open the folder in File Explorer, click the address bar, type powershell, and press Enter.' : pairingPlatform === 'macos' ? 'Open Terminal, type cd followed by a space, drag the extracted folder into Terminal, and press Return.' : 'Use your file manager\'s Open in Terminal action, or change directory to the extracted folder.'}</span></div></li>
        <li><div className="agent-installer-step"><strong>Run the installer</strong><span>Copy this command into {pairingPlatform === 'windows' ? 'PowerShell' : 'Terminal'}:</span><div className="command-field command-field--multiline"><code>{installerCommands}</code><button className="icon-button" type="button" onClick={() => void copy(installerCommands, 'Installer command')} aria-label="Copy installer command"><Icon name="clipboard" size={17} /></button></div></div></li>
        <li><div className="agent-installer-step"><strong>Wait for the readiness check</strong><span>Installation is complete when the output says the Agent is installed and every required <code>doctor</code> check reports <code>OK</code>. Then generate the one-time enrollment code below.</span></div></li>
      </ol>
      <p className="worker-card__hint">The first installation builds the FFmpeg runtime inside Docker and can take several minutes. You do not need to open or edit any configuration file.</p>
    </article>}

    {selectedView === 'workers' && <article className="worker-card enrollment-card">
      <div className="worker-card__heading"><div><h2>Create enrollment code</h2><p>Generate a short-lived secret, then pair one trusted machine.</p></div><span className="worker-card__heading-icon"><Icon name="plus" size={20} /></span></div>
      <div className="agent-readiness-note">
        <Icon name="shield" size={17} />
        <div><strong>Generate only after installation passes</strong><span>The 10-minute timer starts when you select Generate code. The installed helper will ask for the code without storing it in shell history.</span></div>
      </div>
      <form className="worker-enrollment-form" onSubmit={createEnrollment}>
        <label>Worker name<input value={workerName} onChange={(event) => setWorkerName(event.target.value)} maxLength={100} placeholder="Editing Mac" autoComplete="off" aria-describedby="worker-name-hint" required /></label>
        <button className="button button--primary" type="submit" disabled={creating || !workerName.trim()}>{creating ? 'Generating…' : 'Generate code'}</button>
      </form>
      <p className="worker-card__hint" id="worker-name-hint">Use a recognizable computer name. The generated pairing command passes this exact name to the installed Agent.</p>

      {enrollment && <div className="enrollment-secret" aria-live="polite">
        <div className="enrollment-secret__heading"><div><span>One-time enrollment code</span><strong>Copy this now—it is shown only in this dashboard session.</strong></div><button className="button button--secondary" type="button" onClick={() => void copy(enrollment.code, 'Enrollment code')}><Icon name="clipboard" size={16} />Copy code</button></div>
        <code className="enrollment-code">{enrollment.code}</code>
        <p><Icon name="warning" size={15} /><span><strong>Valid once for 10 minutes.</strong> Expires {formatTimestamp(enrollment.expiresAt)}. Generating a code does not save it in this browser.</span></p>
        <div className="pairing-instructions">
          <h3>Pair the local agent</h3>
          <ol>
            <li>Open {pairingPlatform === 'windows' ? 'PowerShell' : 'Terminal'} on the computer where the Agent was installed.</li>
            <li>Copy and run the command below.</li>
            <li>At the hidden prompt, use <strong>Copy code</strong> above and paste the one-time code.</li>
          </ol>
          <div className="pairing-platforms" role="tablist" aria-label="Agent operating system">
            {([
              ['macos', 'macOS (zsh)'],
              ['linux', 'Linux (Bash)'],
              ['windows', 'Windows (PowerShell)'],
            ] as const).map(([value, label]) => <button
              className={pairingPlatform === value ? 'is-active' : ''}
              key={value}
              type="button"
              role="tab"
              aria-selected={pairingPlatform === value}
              onClick={() => setPairingPlatform(value)}
            >{label}</button>)}
          </div>
          <div className="command-field command-field--multiline"><code>{pairingCommands}</code><button className="icon-button" type="button" onClick={() => void copy(pairingCommands, 'Pairing commands')} aria-label="Copy pairing commands"><Icon name="clipboard" size={17} /></button></div>
          <small>The helper stores the long-lived worker token in the private Docker volume and starts the Agent only after pairing succeeds. It should appear Online after its first heartbeat, normally within 30–90 seconds.</small>
        </div>
      </div>}
    </article>}

    {selectedView === 'setup' && <article className="worker-card agent-openai-card">
      <div className="worker-card__heading"><div><h2>Optional: Enable AI subtitles</h2><p>Skip this section if you do not have an OpenAI API key. Video conversion and manually uploaded captions will continue to work.</p></div><span className="worker-card__heading-icon"><Icon name="film" size={20} /></span></div>
      <div className="agent-readiness-note"><Icon name="shield" size={17} /><div><strong>The key never goes to the VPS or browser</strong><span>Run the installed helper on the paired computer. Its hidden prompt keeps the key out of shell history.</span></div></div>
      <div className="pairing-platforms" role="tablist" aria-label="OpenAI setup operating system">
        {([
          ['macos', 'macOS (zsh)'],
          ['linux', 'Linux (Bash)'],
          ['windows', 'Windows (PowerShell)'],
        ] as const).map(([value, label]) => <button className={pairingPlatform === value ? 'is-active' : ''} key={value} type="button" role="tab" aria-selected={pairingPlatform === value} onClick={() => setPairingPlatform(value)}>{label}</button>)}
      </div>
      <div className="command-field command-field--multiline"><code>{openAICommands}</code><button className="icon-button" type="button" onClick={() => void copy(openAICommands, 'OpenAI setup commands')} aria-label="Copy OpenAI setup commands"><Icon name="clipboard" size={17} /></button></div>
      <p className="worker-card__hint">After setup, the worker advertises transcription capability on its next heartbeat, normally within 30 seconds. Billing belongs to the OpenAI account behind this key.</p>
    </article>}

    {selectedView === 'workers' && <article className="worker-card workers-list-card">
      <div className="worker-card__heading"><div><h2>Enrolled workers</h2><p>{overview ? `${overview.counts.totalActiveWorkers} active worker${overview.counts.totalActiveWorkers === 1 ? '' : 's'}` : 'Worker inventory'}</p></div><div className="workers-list-card__actions">{revokedWorkerCount > 0 && <button className="button button--secondary" type="button" aria-pressed={showRevoked} onClick={() => setShowRevoked((value) => !value)}>{showRevoked ? 'Hide revoked' : `Show revoked (${revokedWorkerCount})`}</button>}<span className="worker-card__heading-icon"><Icon name="server" size={20} /></span></div></div>
      {!overview && !loading ? <div className="empty-state"><span className="empty-state__icon"><Icon name="warning" size={25} /></span><strong>Worker inventory unavailable</strong><span>Refresh to try loading the owner-only worker endpoint again.</span></div> : overview && visibleWorkers.length === 0 ? <div className="empty-state"><span className="empty-state__icon"><Icon name="server" size={25} /></span><strong>No active workers</strong><span>Install and pair an Agent, or show revoked workers to review history.</span></div> : overview && <div className="workers-table-wrap"><table className="video-table workers-table"><thead><tr><th>Worker</th><th>State</th><th>Version</th><th>Capabilities</th><th>Last seen</th><th>Created</th><th>Action</th></tr></thead><tbody>{visibleWorkers.map((worker) => {
        const state = workerState(worker);
        return <tr key={worker.id}><td data-label="Worker"><div className="worker-identity"><strong>{worker.name}</strong><span>{worker.id}</span></div></td><td data-label="State"><div className="worker-state"><span className={`status-badge status-badge--${stateTone(state)}`}><span className="status-badge__dot" />{state}</span>{state === 'online' && <small>Activity: {worker.activeJob ? `Busy · ${worker.activeJob.kind} · ${worker.activeJob.stage}` : 'Idle'}</small>}</div></td><td data-label="Version"><code className="worker-version">{worker.version || 'Not reported'}</code></td><td data-label="Capabilities"><div className="worker-capabilities">{worker.capabilities.length ? worker.capabilities.map((capability) => <span key={capability}>{capability}</span>) : <small>None reported</small>}</div></td><td data-label="Last seen"><time dateTime={worker.lastSeenAt ?? undefined}>{formatTimestamp(worker.lastSeenAt)}</time></td><td data-label="Created"><time dateTime={worker.createdAt || undefined}>{formatTimestamp(worker.createdAt)}</time></td><td data-label="Action">{state === 'revoked' ? <span className="worker-revoked-at">{formatTimestamp(worker.revokedAt)}</span> : <button className="button button--danger worker-revoke" type="button" disabled={revokingId === worker.id} onClick={() => void revoke(worker)}>{revokingId === worker.id ? 'Revoking…' : 'Revoke'}</button>}</td></tr>;
      })}</tbody></table></div>}
    </article>}
      </div>
    </article>
  </section>;
}
